/**
 * Canonical resume-ledger section wire values and actions.
 *
 * These strings are a backend contract: `PATCH resume-ledger/:id` validates
 * `section` and `action` against a server-side enum
 * (dproz-professional-service `src/common/resumes.constants.ts`). Import from
 * here instead of writing string literals so a stray value can never diverge.
 *
 * Note the two traps this centralises:
 * - The section is `ACHIEVEMENTS` (writes `ledger.accomplishments`). The value
 *   `ACCOMPLISHMENTS` exists in the server enum but is NOT operationally
 *   supported by PATCH and 403s — never send it.
 * - Several sections map to a differently-named stored property (below).
 */
export const RESUME_SECTIONS = {
	HEADING: "HEADING",
	EXPERIENCE: "EXPERIENCE",
	EDUCATION: "EDUCATION",
	SKILLS: "SKILLS",
	SUMMARY: "SUMMARY",
	REFERENCE: "REFERENCE",
	CERTIFICATION: "CERTIFICATION",
	AFFILIATION: "AFFILIATION",
	ADDITIONAL_INFO: "ADDITIONAL_INFO",
	LANGUAGES: "LANGUAGES",
	ACHIEVEMENTS: "ACHIEVEMENTS",
	PERSONAL: "PERSONAL",
	LINKS: "LINKS",
	CUSTOM: "CUSTOM",
} as const;

export type ResumeSection = (typeof RESUME_SECTIONS)[keyof typeof RESUME_SECTIONS];

export const RESUME_ACTIONS = {
	UPDATE: "UPDATE",
	DELETE: "DELETE",
} as const;

export type ResumeAction = (typeof RESUME_ACTIONS)[keyof typeof RESUME_ACTIONS];

/**
 * Section wire value → the property it writes on the stored `ResumeLedger`.
 * Verified against the backend service switch + schema. Useful when reading a
 * section back out of the canonical ledger returned by PATCH/GET.
 */
export const RESUME_SECTION_PROPERTY: Record<ResumeSection, string> = {
	HEADING: "heading",
	EXPERIENCE: "experience",
	EDUCATION: "education",
	SKILLS: "skills",
	SUMMARY: "professionalSummary",
	REFERENCE: "reference.data",
	CERTIFICATION: "certifications.data",
	AFFILIATION: "affiliations.data",
	ADDITIONAL_INFO: "additionInfo.data",
	LANGUAGES: "languages.data",
	ACHIEVEMENTS: "accomplishments.data",
	PERSONAL: "personlPrivateInfo",
	LINKS: "personalLinks.data",
	CUSTOM: "custom.data",
};
